/**
 * Structural Connection Tool
 * 
 * Creates connection elements between structural members.
 */

import { v4 as uuidv4 } from 'uuid';
import { StructuralConnectionElement } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class StructuralConnectionTool extends BaseTool {
    id = 'structural_connection';
    name = 'Structural Connection';
    cursor = 'crosshair';

    private selectedElementIds: string[] = [];

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const intersects = event.intersections;
        const hit = intersects.find(i => {
            const id = this.findId(i.object);
            return id && id !== 'ground-plane';
        });

        if (hit) {
            const id = this.findId(hit.object)!;
            const element = this.context.bimStore.project.elements.get(id);

            // Check if structural element
            if (element && (element.category === 'Structural Framing' || element.category === 'Structural Columns')) {
                if (!this.selectedElementIds.includes(id)) {
                    this.selectedElementIds.push(id);

                    // Visual feedback (highlight) - simplified
                    console.log(`Selected element: ${id}`);

                    if (this.selectedElementIds.length === 2) {
                        this.createConnection(this.selectedElementIds[0], this.selectedElementIds[1], hit.point);
                        this.selectedElementIds = [];
                        this.context.appStore.setToolPreview(null);
                    }
                }
            }
        }
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        // Preview logic could go here (highlight candidate)
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createConnection(elem1Id: string, elem2Id: string, position: THREE.Vector3) {
        if (!this.context) return;

        const el1 = this.context.bimStore.project.elements.get(elem1Id);
        const el2 = this.context.bimStore.project.elements.get(elem2Id);

        if (!el1 || !el2) return;

        let type: 'Moment Frame' | 'Shear' | 'Splice' = 'Shear'; // Default

        // 1. Get Vectors
        const v1 = this.getElementVector(el1);
        const v2 = this.getElementVector(el2);

        if (v1 && v2) {
            // 2. Calculate Angle
            const dot = v1.dot(v2);
            // dot is cos(angle) if normalized. 
            // 1 or -1 = Parallel/Collinear. 0 = Perpendicular.

            const absDot = Math.abs(dot);

            if (absDot > 0.95) {
                // Parallel/Collinear
                type = 'Splice';
            } else if (absDot < 0.2) {
                // Perpendicular (ish)
                type = 'Shear';
            } else {
                // Oblique
                type = 'Moment Frame';
            }
        }

        const connection: StructuralConnectionElement = {
            id: uuidv4(),
            category: 'Structural Connections',
            type: `${type} Connection`,
            parameters: {
                'Connection Type': type,
                'Approval Status': 'None'
            },
            constraints: [],
            geometryData: {
                position: [position.x, position.y, position.z],
                connectedElementIds: [elem1Id, elem2Id],
                connectionType: type
            },
            analyticalLinks: [
                {
                    startNodeId: elem1Id,
                    endNodeId: elem2Id,
                    stiffness: type === 'Moment Frame' ? 10000 : 5000 // Moment is stiffer
                }
            ]
        };

        this.context.bimStore.addElement(connection);
        console.log(`Created Auto-Connection: ${type}`);
    }

    private getElementVector(element: any): THREE.Vector3 | null {
        if (element.category === 'Structural Framing' && element.geometryData && element.geometryData.start && element.geometryData.end) {
            const start = new THREE.Vector3(...element.geometryData.start);
            const end = new THREE.Vector3(...element.geometryData.end);
            return end.sub(start).normalize();
        } else if (element.category === 'Structural Columns') {
            // Columns are usually Z-up
            return new THREE.Vector3(0, 0, 1);
        }
        return null; // Unknown geometry
    }
}
